import { type ReactNode, type Ref } from 'react'
import { X } from 'lucide-react'
import styles from './LabelBrowser.module.css'

type LabelBrowserProps = {
  actions: ReactNode
  ariaLabel: string
  body: ReactNode
  children?: never
  className?: string | undefined
  closeLabel?: string | undefined
  description?: ReactNode
  onClose: () => void
  panelRef?: Ref<HTMLDivElement> | undefined
  title: ReactNode
  tools?: ReactNode
}

export const labelBrowserClassName = (...classes: Array<string | false | null | undefined>): string =>
  classes
    .filter(Boolean)
    .map((className) => {
      const scopedClassName = styles[className as string]

      return scopedClassName ? `${scopedClassName} ${className}` : className
    })
    .join(' ')

export const LabelBrowser = ({
  actions,
  ariaLabel,
  body,
  className,
  closeLabel = 'Close label browser',
  description,
  onClose,
  panelRef,
  title,
  tools
}: LabelBrowserProps) => (
  <div
    className={labelBrowserClassName('label-browser-backdrop')}
    onClick={(event) => {
      if (event.target === event.currentTarget) {
        onClose()
      }
    }}
    role="presentation"
  >
    <div
      aria-label={ariaLabel}
      aria-modal="true"
      className={labelBrowserClassName('label-browser-panel', className)}
      onClick={(event) => event.stopPropagation()}
      onWheel={(event) => event.stopPropagation()}
      ref={panelRef}
      role="dialog"
    >
      <div className={labelBrowserClassName('label-browser-heading')}>
        <div>
          <h3>{title}</h3>
          {description && <p>{description}</p>}
        </div>
        <button
          aria-label={closeLabel}
          className="modal-close-button"
          onClick={onClose}
          type="button"
        >
          <X size={18} />
        </button>
      </div>
      {tools && <div className={labelBrowserClassName('label-browser-tools')}>{tools}</div>}
      {body}
      <div className={labelBrowserClassName('label-browser-actions')}>{actions}</div>
    </div>
  </div>
)
